import React from 'react';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { useTheme } from '../../../../contexts/ThemeContext';

/**
 * StatCard - Reusable card for displaying a single statistic
 *
 * @param {string} label - Stat label (e.g., "Win Rate", "Total Wins")
 * @param {string|number} value - Stat value
 * @param {React.Component} icon - Lucide icon component
 * @param {string} trend - Trend direction: 'up' | 'down' | 'neutral'
 * @param {string} trendValue - Trend value to display (e.g., "+5", "-2")
 * @param {string} subtitle - Optional subtitle/secondary text
 * @param {string} color - Optional color theme: 'primary' | 'success' | 'warning' | 'danger'
 */
const StatCard = ({
  label,
  value,
  icon: Icon,
  trend,
  trendValue,
  subtitle,
  color = 'primary'
}) => {
  const { theme } = useTheme();

  const colorClasses = {
    primary: 'text-blue-500',
    success: 'text-green-500',
    warning: 'text-yellow-500',
    danger: 'text-red-500'
  };

  const trendColors = {
    up: 'text-green-500',
    down: 'text-red-500',
    neutral: theme.textMuted
  };

  const trendIcon = trend === 'up' ? TrendingUp : trend === 'down' ? TrendingDown : Minus;
  const TrendIcon = trendIcon;

  return (
    <div className={`${theme.card} rounded-lg border ${theme.border} p-4`}>
      <div className="flex items-start justify-between mb-2">
        <div>
          <p className={`text-xs font-medium ${theme.textMuted} uppercase tracking-wide`}>
            {label}
          </p>
          <p className={`text-2xl font-bold ${theme.text} mt-1`}>
            {value}
          </p>
          {subtitle && (
            <p className={`text-xs ${theme.textMuted} mt-1`}>
              {subtitle}
            </p>
          )}
        </div>
        {Icon && (
          <div className={`p-2 rounded-lg ${theme.backgroundSecondary}`}>
            <Icon className={`w-5 h-5 ${colorClasses[color]}`} />
          </div>
        )}
      </div>

      {trend && trendValue && (
        <div className="flex items-center gap-1 mt-2">
          <TrendIcon className={`w-4 h-4 ${trendColors[trend]}`} />
          <span className={`text-sm font-medium ${trendColors[trend]}`}>
            {trendValue}
          </span>
          <span className={`text-xs ${theme.textMuted} ml-1`}>
            vs last period
          </span>
        </div>
      )}
    </div>
  );
};

export default StatCard;
